import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    Divider,
    Switch,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper
} from '@mui/material';
import {
    Link as LinkIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    Event as EventIcon,
    Settings as SettingsIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const AdminCalendly = () => {
    const { t } = useTranslation();

    // State for Calendly configuration
    const [config, setConfig] = useState({
        calendlyUrl: localStorage.getItem('calendly_url') || '',
        enabled: localStorage.getItem('calendly_enabled') === 'true',
        primaryColor: localStorage.getItem('calendly_primary_color') || '#D4B5B0',
        textColor: localStorage.getItem('calendly_text_color') || '#1A1A1A',
        backgroundColor: localStorage.getItem('calendly_background_color') || '#FFFFFF'
    });

    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [events, setEvents] = useState(() => {
        try {
            const raw = localStorage.getItem('calendly_events');
            return raw ? JSON.parse(raw) : [];
        } catch (_) {
            return [];
        }
    });

    // Example Calendly URLs for guidance
    const exampleUrls = [
        'https://calendly.com/your-username',
        'https://calendly.com/your-username/30min',
        'https://calendly.com/your-username/consultation'
    ];

    // Validation for Calendly URL
    const isValidCalendlyUrl = (url) => {
        if (!url) return false;
        const trimmed = url.trim();
        // פרופיל או סוג אירוע בלבד (לא new-meeting / scheduled_events / app/...)
        const pattern = /^https:\/\/calendly\.com\/[a-z0-9_-]+(\/[a-z0-9_-]+)?(\?.*)?$/i;
        if (!pattern.test(trimmed)) return false;
        const badSegments = ['new-meeting', 'scheduled_events', 'app/'];
        return !badSegments.some(seg => trimmed.includes(seg));
    };

    const sanitizeCalendlyUrl = (url) => {
        if (!url) return '';
        const trimmed = url.trim();
        const userMatch = trimmed.match(/^https:\/\/calendly\.com\/([a-z0-9_-]+)(?:\/([a-z0-9_-]+))?/i);
        if (!userMatch) return trimmed;
        const username = userMatch[1];
        const event = userMatch[2];
        const badSegments = ['new-meeting', 'scheduled_events'];
        if (!event || badSegments.includes(event) || trimmed.includes('/app/')) {
            // החזרה ללינק פרופיל תקין
            return `https://calendly.com/${username}`;
        }
        return `https://calendly.com/${username}/${event}`;
    };

    const handleSave = async () => {
        const trimmedUrl = (config.calendlyUrl || '').trim();
        if (config.enabled && !isValidCalendlyUrl(trimmedUrl)) {
            setSaveMessage({
                type: 'error',
                text: 'כתובת Calendly לא תקינה. יש להזין כתובת המתחילה ב-https://calendly.com/'
            });
            return;
        }

        setIsSaving(true);

        try {
            // Save to localStorage
            localStorage.setItem('calendly_url', trimmedUrl);
            localStorage.setItem('calendly_enabled', String(config.enabled));
            localStorage.setItem('calendly_primary_color', config.primaryColor);
            localStorage.setItem('calendly_text_color', config.textColor);
            localStorage.setItem('calendly_background_color', config.backgroundColor);

            // Also save to backend via environment/settings API if needed
            // await settingsService.updateCalendlyConfig(config);

            setSaveMessage({
                type: 'success',
                text: 'הגדרות Calendly נשמרו בהצלחה!'
            });

            // Trigger event for other components to refresh
            window.dispatchEvent(new CustomEvent('calendly-config-updated'));
            // Update local state with trimmed url so הסטטוס יתעדכן
            setConfig((prev) => ({ ...prev, calendlyUrl: trimmedUrl }));
        } catch (error) {
            console.error('Failed to save Calendly config:', error);
            setSaveMessage({
                type: 'error',
                text: 'שגיאה בשמירת ההגדרות'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleTest = () => {
        if (!isValidCalendlyUrl(config.calendlyUrl)) {
            setSaveMessage({
                type: 'error',
                text: 'אנא הזיני כתובת Calendly תקינה לפני הבדיקה'
            });
            return;
        }
        setTestDialogOpen(true);
    };

    const openCalendlyDashboard = () => {
        window.open('https://calendly.com/app/scheduled_events/user/me', '_blank');
    };

    const features = [
        { title: 'סנכרון אוטומטי', desc: 'תורים מסונכרנים אוטומטית מ-Calendly', available: true },
        { title: 'התראות', desc: 'התראות על תורים חדשים ומבוטלים', available: true },
        { title: 'עיצוב מותאם', desc: 'התאמת צבעים לעיצוב האתר', available: true },
        { title: 'זמינות דינמית', desc: 'ניהול שעות עבודה וחריגות', available: true }
    ];

    // Auto-persist to localStorage on changes (debounced)
    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                const trimmedUrl = (config.calendlyUrl || '').trim();
                const sanitized = sanitizeCalendlyUrl(trimmedUrl);
                localStorage.setItem('calendly_url', sanitized);
                localStorage.setItem('calendly_enabled', String(config.enabled));
                localStorage.setItem('calendly_primary_color', config.primaryColor);
                localStorage.setItem('calendly_text_color', config.textColor);
                localStorage.setItem('calendly_background_color', config.backgroundColor);
                localStorage.setItem('calendly_events', JSON.stringify(events || []));
                window.dispatchEvent(new CustomEvent('calendly-config-updated'));
                if (sanitized !== trimmedUrl) {
                    setConfig((prev) => ({ ...prev, calendlyUrl: sanitized }));
                }
            } catch (_) {
                // ignore
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [config.enabled, config.calendlyUrl, config.primaryColor, config.textColor, config.backgroundColor, events]);

    const addEvent = () => {
        setEvents([...events, { label: '', url: '' }]);
    };

    const updateEvent = (index, field, value) => {
        const next = events.slice();
        next[index] = { ...next[index], [field]: value };
        setEvents(next);
    };

    const removeEvent = (index) => {
        const next = events.filter((_, i) => i !== index);
        setEvents(next);
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    אינטגרציה עם Calendly
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    נהלי את חיבור האתר למערכת Calendly לניהול תורים
                </Typography>
            </Box>

            {/* Save Message */}
            {saveMessage.text && (
                <Alert
                    severity={saveMessage.type}
                    onClose={() => setSaveMessage({ type: '', text: '' })}
                    sx={{ mb: 3 }}
                >
                    {saveMessage.text}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Main Configuration */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    הגדרות חיבור
                                </Typography>
                            </Box>

                            {/* Enable/Disable Calendly */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.enabled}
                                        onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="subtitle2">
                                            הפעלת Calendly באתר
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {config.enabled ? 'לקוחות יכולים לקבוע תורים דרך Calendly' : 'מערכת Calendly כבויה'}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ mb: 3 }}
                            />

                            <Divider sx={{ mb: 3 }} />

                            {/* Calendly URL */}
                            <TextField
                                fullWidth
                                label="כתובת Calendly"
                                placeholder="https://calendly.com/your-username/event-type"
                                value={config.calendlyUrl}
                                onChange={(e) => setConfig({ ...config, calendlyUrl: e.target.value })}
                                disabled={!config.enabled}
                                error={config.enabled && config.calendlyUrl && !isValidCalendlyUrl(config.calendlyUrl)}
                                helperText={
                                    config.enabled && config.calendlyUrl && !isValidCalendlyUrl(config.calendlyUrl)
                                        ? 'כתובת לא תקינה. נא להשתמש בקישור פרופיל/אירוע (לא new-meeting / app / scheduled_events).'
                                        : 'העתיקי את הקישור לאירוע מ-Calendly שלך'
                                }
                                InputProps={{
                                    endAdornment: config.calendlyUrl && isValidCalendlyUrl(config.calendlyUrl) && (
                                        <CheckIcon sx={{ color: 'success.main' }} />
                                    )
                                }}
                                sx={{ mb: 2 }}
                            />

                            {/* Example URLs */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    דוגמאות לכתובות:
                                </Typography>
                                {exampleUrls.map((url, index) => (
                                    <Chip
                                        key={index}
                                        label={url}
                                        size="small"
                                        variant="outlined"
                                        sx={{ mr: 1, mb: 1, fontSize: '0.75rem' }}
                                    />
                                ))}
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            {/* Color Customization */}
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                התאמת צבעים
                            </Typography>

                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                            צבע ראשי
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <input
                                                type="color"
                                                value={config.primaryColor}
                                                onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                                                disabled={!config.enabled}
                                                style={{
                                                    width: '50px',
                                                    height: '40px',
                                                    border: '1px solid #E0E0E0',
                                                    borderRadius: '4px',
                                                    cursor: config.enabled ? 'pointer' : 'not-allowed'
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                value={config.primaryColor}
                                                disabled={!config.enabled}
                                                onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                                                sx={{ flex: 1 }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                            צבע טקסט
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <input
                                                type="color"
                                                value={config.textColor}
                                                onChange={(e) => setConfig({ ...config, textColor: e.target.value })}
                                                disabled={!config.enabled}
                                                style={{
                                                    width: '50px',
                                                    height: '40px',
                                                    border: '1px solid #E0E0E0',
                                                    borderRadius: '4px',
                                                    cursor: config.enabled ? 'pointer' : 'not-allowed'
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                value={config.textColor}
                                                disabled={!config.enabled}
                                                onChange={(e) => setConfig({ ...config, textColor: e.target.value })}
                                                sx={{ flex: 1 }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                            צבע רקע
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <input
                                                type="color"
                                                value={config.backgroundColor}
                                                onChange={(e) => setConfig({ ...config, backgroundColor: e.target.value })}
                                                disabled={!config.enabled}
                                                style={{
                                                    width: '50px',
                                                    height: '40px',
                                                    border: '1px solid #E0E0E0',
                                                    borderRadius: '4px',
                                                    cursor: config.enabled ? 'pointer' : 'not-allowed'
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                value={config.backgroundColor}
                                                disabled={!config.enabled}
                                                onChange={(e) => setConfig({ ...config, backgroundColor: e.target.value })}
                                                sx={{ flex: 1 }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    sx={{
                                        background: 'linear-gradient(135deg, #D4B5B0 0%, #C9A9A4 100%)',
                                        px: 4
                                    }}
                                >
                                    {isSaving ? 'שומר...' : 'שמירת שינויים'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleTest}
                                    disabled={!config.enabled || !isValidCalendlyUrl(config.calendlyUrl)}
                                    startIcon={<OpenInNewIcon />}
                                >
                                    בדיקת תצוגה
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={openCalendlyDashboard}
                                    startIcon={<SettingsIcon />}
                                >
                                    פתח Calendly Dashboard
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Multiple Events */}
                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    אירועי Calendly (אופציונלי)
                                </Typography>
                                <Button variant="outlined" onClick={addEvent} disabled={!config.enabled}>
                                    הוספת אירוע
                                </Button>
                            </Box>
                            {events.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    ניתן להוסיף כמה אירועים (למשל טיפולים שונים). כל אירוע מזוהה לפי שם ותציגי אותו לבחירה בדף קביעת התור.
                                </Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    {events.map((ev, index) => (
                                        <Grid item xs={12} key={index}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        label="שם האירוע"
                                                        value={ev.label}
                                                        onChange={(e) => updateEvent(index, 'label', e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        disabled={!config.enabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={7}>
                                                    <TextField
                                                        label="קישור האירוע"
                                                        placeholder="https://calendly.com/your-username/event-type"
                                                        value={ev.url}
                                                        onChange={(e) => updateEvent(index, 'url', e.target.value)}
                                                        fullWidth
                                                        size="small"
                                                        disabled={!config.enabled}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <Button color="error" variant="text" onClick={() => removeEvent(index)}>
                                                        מחיקה
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </CardContent>
                    </Card>

                    {/* Instructions Card */}
                    <Card sx={{ mt: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                מדריך התקנה
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                <strong>שלב 1:</strong> היכנסי לחשבון Calendly שלך ב-https://calendly.com
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                <strong>שלב 2:</strong> בחרי את סוג האירוע שברצונך להטמיע (למשל "תור ייעוץ 30 דקות")
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                <strong>שלב 3:</strong> העתיקי את הקישור לאירוע (למשל: https://calendly.com/your-name/consultation)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                <strong>שלב 4:</strong> הדביקי את הקישור בשדה "כתובת Calendly" למעלה
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>שלב 5:</strong> התאימי את הצבעים לעיצוב האתר ושמרי את השינויים
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Sidebar - Features & Status */}
                <Grid item xs={12} md={4}>
                    {/* Status Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    סטטוס
                                </Typography>
                                <Chip
                                    label={config.enabled && (isValidCalendlyUrl(config.calendlyUrl) || (events && events.length > 0)) ? 'פעיל' : 'לא פעיל'}
                                    color={config.enabled && (isValidCalendlyUrl(config.calendlyUrl) || (events && events.length > 0)) ? 'success' : 'default'}
                                    size="small"
                                />
                            </Box>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        {config.enabled ? (
                                            <CheckIcon sx={{ color: 'success.main' }} />
                                        ) : (
                                            <CloseIcon sx={{ color: 'error.main' }} />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="מערכת מופעלת"
                                        secondary={config.enabled ? 'כן' : 'לא'}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        {config.calendlyUrl && isValidCalendlyUrl(config.calendlyUrl) ? (
                                            <CheckIcon sx={{ color: 'success.main' }} />
                                        ) : (
                                            <CloseIcon sx={{ color: 'error.main' }} />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="כתובת מוגדרת"
                                        secondary={config.calendlyUrl ? 'כן' : 'לא'}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        {(events && events.length > 0) ? (
                                            <CheckIcon sx={{ color: 'success.main' }} />
                                        ) : (
                                            <CloseIcon sx={{ color: 'error.main' }} />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="אירועים מרובים"
                                        secondary={(events && events.length > 0) ? `${events.length} אירועים מוגדרים` : 'לא הוגדרו אירועים'}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>

                    {/* Features Card */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                תכונות זמינות
                            </Typography>
                            <List dense>
                                {features.map((feature, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            {feature.available ? (
                                                <CheckIcon sx={{ color: 'success.main' }} />
                                            ) : (
                                                <CloseIcon sx={{ color: 'text.disabled' }} />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={feature.title}
                                            secondary={feature.desc}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Test Dialog */}
            <Dialog
                open={testDialogOpen}
                onClose={() => setTestDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    תצוגת מקדימה - Calendly
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ minHeight: 600 }}>
                        {config.calendlyUrl && (
                            <iframe
                                src={`${config.calendlyUrl}?embed_domain=localhost&embed_type=Inline&primary_color=${config.primaryColor.replace('#', '')}&text_color=${config.textColor.replace('#', '')}&background_color=${config.backgroundColor.replace('#', '')}`}
                                width="100%"
                                height="600"
                                frameBorder="0"
                                title="Calendly Preview"
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestDialogOpen(false)}>
                        סגור
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminCalendly;

